import type { ReactNode } from 'react';

/**
 * The bottom-right corner (2026-10-28). The Read-only pill, the Guide
 * launcher and the NADC.AI button were each fixed to the same corner on
 * their own, so the Guide and NADC.AI landed on top of each other. They now
 * share one fixed column, top to bottom in that order, resting at
 * --corner-base and lifted by --corner-lift (styles/index.css).
 *
 * The column publishes its height as --corner-stack-height, so anything that
 * floats along the bottom (toasts, the update banner, the desk upload tray)
 * can sit above it rather than over it. Pointer events are off on the
 * column itself, so its gaps let clicks through; each item turns them back
 * on (pointer-events-auto).
 */
export function CornerStack({ children }: { children: ReactNode }) {
  return (
    <div
      ref={publishHeight}
      data-testid="corner-stack"
      className="pointer-events-none fixed right-4 z-40 flex flex-col items-end gap-2 print:hidden"
      style={{ bottom: 'calc(var(--corner-base, 1rem) + var(--corner-lift, 0px))' }}
    >
      {children}
    </div>
  );
}

let stackObserver: ResizeObserver | null = null;

function publishHeight(el: HTMLDivElement | null) {
  stackObserver?.disconnect();
  stackObserver = null;
  const root = document.documentElement;
  if (!el) {
    root.style.removeProperty('--corner-stack-height');
    return;
  }
  const measure = () => root.style.setProperty('--corner-stack-height', `${el.offsetHeight}px`);
  measure();
  if (typeof ResizeObserver !== 'undefined') {
    stackObserver = new ResizeObserver(measure);
    stackObserver.observe(el);
  }
}
